import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useGameStore from '../../store/gameStore';
import MarkdownLite from '../MarkdownLite';
import {
  Users, Truck, FileText, Play, ChevronRight, ChevronLeft,
  Wallet, HeartHandshake, Award, Sparkles, ArrowRight, Rocket,
} from 'lucide-react';

/**
 * Onboarding tour — refonte visuelle complète 18/06/2026 (design agent).
 *
 *   - Étapes 0 et 7 : modal immersif plein écran (glassmorphism centré,
 *     animate-scale-in) — accueil + conclusion.
 *   - Étapes 1-6 : carte flottante ancrée (glass card, animate-float-in)
 *     avec barre de progression SEGMENTÉE dans le header.
 *   - Spotlight 4 quadrants (bg-ink-900/60 + blur léger) + anneau accent
 *     animé `animate-pulse-ring` autour de l'élément cible.
 *   - Contenu actualisé : péages sur les contrats, TVA, conseils
 *     contextuels, conseillers stratégiques, bouton « Signaler un bug ».
 *   - Skip disponible à CHAQUE étape, navigation manuelle Préc./Suivant,
 *     auto-advance conservé quand l'action est réalisée.
 *   - `pointer-events-none` sur le wrapper : le jeu reste interactif.
 */
const TUTORIAL_STEPS = [
  {
    id: 0,
    title: 'Bienvenue dans HST',
    message: "Vous prenez la direction d'une **société de transport routier**. En haut de l'écran : votre **trésorerie**, le **jour de jeu** et votre **réputation** — vos trois signes vitaux.\n\nCe tour en 8 étapes vous mène jusqu'à votre **premier euro encaissé**.",
    targetRoute: '/',
    icon: Rocket,
    position: 'center',
  },
  {
    id: 1,
    title: 'Recrutez votre premier chauffeur',
    message: "Pas de chauffeur, pas de revenu. Chaque profil a une **spécialité** (Messagerie, Vrac, Frigo, BTP…) qui doit correspondre à vos futurs contrats. Un salarié type coûte ~ 2 200 €/mois chargé.",
    hint: "Onglet RH → choisissez un profil → négociez le contrat",
    targetRoute: '/hr',
    targetAction: 'hire',
    icon: Users,
    highlightSelector: '[data-testid="nav-hr"], [data-testid="mobile-nav-hr"]',
    position: 'right',
    spotlight: true,
  },
  {
    id: 2,
    title: 'Assignez-le à votre véhicule',
    message: "Un chauffeur reste improductif tant qu'il n'est pas au volant. Direction la **Flotte** pour faire le lien chauffeur ↔ véhicule.",
    hint: "Onglet Flotte → carte véhicule → assigner",
    targetRoute: '/fleet',
    targetAction: 'assign',
    icon: Truck,
    highlightSelector: '[data-testid="nav-fleet"], [data-testid="mobile-nav-fleet"]',
    position: 'right',
    spotlight: true,
  },
  {
    id: 3,
    title: 'Acceptez votre premier contrat',
    message: "Retour au **Tableau de bord**. Chaque carte contrat affiche le **revenu/jour**, la durée… et les **péages estimés** au-delà de 30 km. La marge réelle, c'est revenu − charges − péages.",
    hint: "Préférez les contrats courts (< 15 j) au début",
    targetRoute: '/',
    targetAction: 'contract',
    icon: FileText,
    highlightSelector: '[data-testid="nav-dashboard"], [data-testid="mobile-nav-dashboard"]',
    position: 'right',
    spotlight: true,
  },
  {
    id: 4,
    title: 'Avancez le temps',
    message: "Bouton **Jour suivant** en bas du Tableau de bord. Vos chauffeurs partent en mission, les revenus tombent, les charges aussi — le compteur de trésorerie bouge en temps réel.",
    targetRoute: '/',
    targetAction: 'nextday',
    icon: Play,
    highlightSelector: '[data-testid="next-day-button"]',
    position: 'top',
    spotlight: true,
  },
  {
    id: 5,
    title: 'Lisez votre trésorerie',
    message: "**Vert = revenus encaissés. Rouge = charges** (salaires, carburant, entretien, péages). Gardez un œil sur la **TVA** : celle collectée sur vos factures n'est pas à vous — elle se déclare dans l'onglet Fiscalité.",
    targetRoute: '/',
    icon: Wallet,
    autoAdvanceAfterDays: 3,
    position: 'top',
  },
  {
    id: 6,
    title: 'Surveillez vos chauffeurs',
    message: "Onglet RH → chaque chauffeur a une jauge **satisfaction** et **fatigue**. Sous 45 % de satisfaction, il pense à démissionner. Au-delà de 75 % de fatigue, il refuse de partir en mission.",
    hint: "Bonus (150 €) ou repos forcé pour redresser",
    targetRoute: '/hr',
    icon: HeartHandshake,
    targetAction: 'visit-hr',
    position: 'right',
  },
  {
    id: 7,
    title: 'Vous maîtrisez les bases',
    message: "Continuez à recruter, investir et signer des contrats. Des **conseils contextuels** apparaîtront à chaque nouvelle mécanique (fiscalité, crédit, péages, intérim, M&A…), et vos **conseillers stratégiques** analysent votre gestion en continu.\n\nLe **manuel pédagogique** reste accessible en bas à droite — et si quelque chose cloche, le bouton **Signaler un bug** est là pour ça.",
    targetRoute: null,
    icon: Award,
    position: 'center',
  },
];

const TutorialSpotlight = ({ selector }) => {
  const [rect, setRect] = useState(null);

  // Recompute the spotlight rect every 250 ms — handles route changes,
  // collapsed sidebar, mobile bottom nav, animation glitches.
  useEffect(() => {
    if (!selector) {
      setRect(null);
      return undefined;
    }
    const update = () => {
      const el = document.querySelector(selector);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({
        top: Math.max(0, r.top - 6),
        left: Math.max(0, r.left - 6),
        width: r.width + 12,
        height: r.height + 12,
      });
    };
    update();
    const id = setInterval(update, 250);
    window.addEventListener('resize', update);
    return () => { clearInterval(id); window.removeEventListener('resize', update); };
  }, [selector]);

  if (!rect) return null;

  // 4 quadrants (top / left / right / bottom) — dim + léger blur, carving
  // out the target rect. Pointer-events-none : le jeu reste cliquable.
  const dim = 'fixed bg-ink-900/60 backdrop-blur-[2px] z-[78] pointer-events-none transition-all duration-500';
  return (
    <>
      <div className={dim} style={{ top: 0, left: 0, right: 0, height: rect.top }} />
      <div className={dim} style={{ top: rect.top, left: 0, width: rect.left, height: rect.height }} />
      <div className={dim} style={{ top: rect.top, left: rect.left + rect.width, right: 0, height: rect.height }} />
      <div className={dim} style={{ top: rect.top + rect.height, left: 0, right: 0, bottom: 0 }} />
      {/* Anneau accent pulsé autour de la cible (pointer-events-none). */}
      <div
        className="fixed z-[78] pointer-events-none rounded-lg ring-2 ring-accent animate-pulse-ring"
        style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
        data-testid="tutorial-spotlight-ring"
      />
    </>
  );
};

/** Barre de progression segmentée — 1 segment par étape. */
const SegmentedProgress = ({ current, total }) => (
  <div className="flex w-full gap-1 h-1.5 rounded-full overflow-hidden" data-testid="tutorial-progress">
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={i}
        className={`flex-1 h-full rounded-full transition-colors duration-300 ${
          i < current ? 'bg-accent/40' : i === current ? 'bg-accent' : 'bg-ink-700'
        }`}
      />
    ))}
  </div>
);

export const TutorialOverlay = () => {
  const tutorialStep = useGameStore(s => s.tutorialStep);
  const tutorialComplete = useGameStore(s => s.tutorialComplete);
  const advanceTutorial = useGameStore(s => s.advanceTutorial);
  const goBackTutorial = useGameStore(s => s.goBackTutorial);
  const skipTutorial = useGameStore(s => s.skipTutorial);
  const drivers = useGameStore(s => s.drivers);
  const vehicles = useGameStore(s => s.vehicles);
  const activeContracts = useGameStore(s => s.activeContracts);
  const totalDays = useGameStore(s => s.totalDays);
  const navigate = useNavigate();
  const location = useLocation();

  const shouldShow = !tutorialComplete
    && tutorialStep !== undefined && tutorialStep !== null
    && tutorialStep < TUTORIAL_STEPS.length;

  const step = shouldShow ? TUTORIAL_STEPS[tutorialStep] : null;

  // Auto-advance gated on actual game state — performing the action is
  // a SHORTCUT to skip ahead. Manual "Suivant"/"Précédent" always work.
  useEffect(() => {
    if (!shouldShow) return undefined;
    let shouldAdvance = false;
    if (tutorialStep === 1 && drivers.length > 0) shouldAdvance = true;
    if (tutorialStep === 2 && vehicles.some(v => v.assignedDriverId)) shouldAdvance = true;
    if (tutorialStep === 3 && activeContracts.length > 0) shouldAdvance = true;
    if (tutorialStep === 4 && totalDays > 0) shouldAdvance = true;
    // step 5 = "watch treasury" — advance after a few days of play
    if (tutorialStep === 5 && totalDays >= (step?.autoAdvanceAfterDays || 3)) shouldAdvance = true;
    // step 6 = "visit HR" — advance as soon as the player navigates to /hr
    if (tutorialStep === 6 && location.pathname === '/hr') shouldAdvance = true;

    if (shouldAdvance) {
      const timer = setTimeout(() => advanceTutorial(), 600);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [drivers.length, vehicles, activeContracts.length, totalDays, tutorialStep, shouldShow, advanceTutorial, location.pathname, step]);

  const handleNext = useCallback(() => {
    if (!step) return;
    if (tutorialStep === TUTORIAL_STEPS.length - 1) {
      skipTutorial();
      return;
    }
    advanceTutorial();
    const nextStep = TUTORIAL_STEPS[tutorialStep + 1];
    if (nextStep?.targetRoute && location.pathname !== nextStep.targetRoute) {
      navigate(nextStep.targetRoute);
    }
  }, [step, tutorialStep, advanceTutorial, skipTutorial, navigate, location.pathname]);

  const handlePrev = useCallback(() => {
    if (tutorialStep <= 0) return;
    goBackTutorial();
    const prevStep = TUTORIAL_STEPS[tutorialStep - 1];
    if (prevStep?.targetRoute && location.pathname !== prevStep.targetRoute) {
      navigate(prevStep.targetRoute);
    }
  }, [tutorialStep, goBackTutorial, navigate, location.pathname]);

  const handleNav = useCallback(() => {
    if (step?.targetRoute && location.pathname !== step.targetRoute) {
      navigate(step.targetRoute);
    }
  }, [step, location.pathname, navigate]);

  if (!shouldShow || !step) return null;
  const Icon = step.icon;
  const isLastStep = tutorialStep === TUTORIAL_STEPS.length - 1;
  const isFirstStep = tutorialStep === 0;
  const isCentered = step.position === 'center';
  const showSpotlight = step.spotlight === true && !isCentered;

  // ── Étapes immersives (accueil / conclusion) ─────────────────────────
  if (isCentered) {
    return (
      <>
        {/* Dim visuel plein écran — pointer-events-none : rien n'est bloqué. */}
        <div className="fixed inset-0 z-[79] bg-ink-900/70 backdrop-blur-sm pointer-events-none" />
        <div className="fixed inset-0 z-[80] pointer-events-none flex items-center justify-center p-4" data-testid="tutorial-overlay">
          <div
            className="pointer-events-auto w-full max-w-xl rounded-2xl border border-ink-600/50 bg-ink-800/95 backdrop-blur-2xl shadow-2xl shadow-black/60 overflow-hidden animate-scale-in"
            data-testid="tutorial-card"
          >
            <div className="px-8 pt-8 sm:px-12 sm:pt-10">
              <SegmentedProgress current={tutorialStep} total={TUTORIAL_STEPS.length} />
            </div>
            <div className="p-8 sm:p-12 pt-6 sm:pt-8 flex flex-col items-center text-center gap-5">
              <div className="w-20 h-20 rounded-2xl bg-accent/10 border border-accent/20 flex items-center justify-center animate-float-in">
                <Icon className="w-10 h-10 text-accent" />
              </div>
              <div className="space-y-1">
                <span className="text-[11px] uppercase tracking-[0.25em] text-slate-500 font-mono">
                  {isFirstStep ? 'Prise de poste' : 'Tour terminé'} — étape {tutorialStep + 1}/{TUTORIAL_STEPS.length}
                </span>
                <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground tracking-tight" data-testid="tutorial-step-title">
                  {step.title}
                </h2>
              </div>
              <MarkdownLite
                text={step.message}
                className="text-sm sm:text-base text-slate-300 leading-relaxed max-w-md whitespace-pre-line"
                strongClassName="text-foreground font-semibold"
                testId="tutorial-step-message"
              />
              <div className="mt-2 flex items-center gap-3">
                {!isFirstStep && (
                  <button
                    onClick={handlePrev}
                    className="px-4 py-2.5 rounded-full bg-ink-700 hover:bg-ink-600 text-foreground text-sm font-medium transition-colors duration-200 flex items-center gap-1"
                    data-testid="tutorial-prev-btn"
                    aria-label="Étape précédente"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Préc.
                  </button>
                )}
                <button
                  onClick={handleNext}
                  className="px-6 py-2.5 rounded-full bg-accent text-white text-sm font-semibold tracking-wide shadow-glow-accent hover:shadow-[0_0_20px_rgba(59,130,246,0.6)] hover:-translate-y-0.5 transition-[transform,box-shadow,background-color] duration-200 flex items-center gap-1.5"
                  data-testid="tutorial-next-btn"
                >
                  {isLastStep ? 'Lancer mon entreprise' : 'Commencer le tour'}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={skipTutorial}
                className="text-xs text-slate-500 hover:text-slate-300 transition-colors duration-200"
                data-testid="skip-tutorial-btn"
              >
                Passer le tutoriel
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Étapes ancrées (cartes glass flottantes) ─────────────────────────
  return (
    <>
      {showSpotlight && <TutorialSpotlight selector={step.highlightSelector} />}

      <div
        className={`fixed z-[80] pointer-events-none ${step.position === 'top'
          ? 'top-20 right-4 md:top-4 md:right-4'
          : 'bottom-20 md:bottom-4 right-4'}`}
        data-testid="tutorial-overlay"
      >
        <div className="w-80 sm:w-96 max-w-[92vw] pointer-events-auto">
          <div
            className="rounded-xl border border-ink-600/50 bg-ink-800/85 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden animate-float-in"
            data-testid="tutorial-card"
          >
            {/* Header : progression segmentée + titre */}
            <div className="px-5 pt-5 pb-2 flex flex-col gap-3">
              <SegmentedProgress current={tutorialStep} total={TUTORIAL_STEPS.length} />
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4.5 h-4.5 text-accent" style={{ width: 18, height: 18 }} />
                  </div>
                  <h4 className="text-base font-display font-semibold text-foreground tracking-tight truncate" data-testid="tutorial-step-title">
                    {step.title}
                  </h4>
                </div>
                <span className="text-slate-500 text-[10px] font-mono flex-shrink-0">{tutorialStep + 1}/{TUTORIAL_STEPS.length}</span>
              </div>
            </div>

            {/* Body */}
            <div className="px-5 py-2">
              <MarkdownLite
                text={step.message}
                className="text-sm text-slate-300 leading-relaxed"
                strongClassName="text-foreground font-semibold"
                testId="tutorial-step-message"
              />
              {step.hint && (
                <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-accent/20 bg-accent/10 p-3">
                  <Sparkles className="w-3.5 h-3.5 text-accent flex-shrink-0 mt-0.5" />
                  <span className="text-xs font-medium text-accent-hover leading-snug">{step.hint}</span>
                </div>
              )}
            </div>

            {/* Footer : skip à gauche, préc./y aller/suivant à droite */}
            <div className="px-5 py-4 mt-2 border-t border-ink-600/30 bg-ink-800/40 flex items-center justify-between gap-2">
              <button
                onClick={skipTutorial}
                className="text-xs text-slate-500 hover:text-slate-300 transition-colors duration-200 flex-shrink-0"
                data-testid="skip-tutorial-btn"
              >
                Passer le tutoriel
              </button>
              <div className="flex gap-1.5 flex-wrap justify-end">
                {!isFirstStep && (
                  <button
                    onClick={handlePrev}
                    className="text-xs bg-ink-700 hover:bg-ink-600 text-foreground px-3 py-1.5 rounded-full font-medium transition-colors duration-200 flex items-center gap-0.5"
                    data-testid="tutorial-prev-btn"
                    aria-label="Étape précédente"
                  >
                    <ChevronLeft className="w-3 h-3" />
                    Préc.
                  </button>
                )}
                {step.targetRoute && location.pathname !== step.targetRoute && (
                  <button
                    onClick={handleNav}
                    className="text-xs bg-ink-700 hover:bg-ink-600 text-foreground px-3 py-1.5 rounded-full font-medium transition-colors duration-200 flex items-center gap-1"
                    data-testid="tutorial-navigate-btn"
                  >
                    Y aller
                    <ArrowRight className="w-3 h-3" />
                  </button>
                )}
                <button
                  onClick={handleNext}
                  className="text-xs bg-accent text-white px-4 py-1.5 rounded-full font-semibold tracking-wide shadow-glow-accent hover:shadow-[0_0_18px_rgba(59,130,246,0.6)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200 flex items-center gap-0.5"
                  data-testid="tutorial-next-btn"
                >
                  {isLastStep ? 'Terminer' : (
                    <>
                      Suivant
                      <ChevronRight className="w-3 h-3" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TutorialOverlay;
